/**
 * MCP connector OAuth login bridge.
 *
 * When MCG's OAuth 2.1 authorization server (the backend `/authorize` endpoint) needs the
 * user to sign in, it redirects the browser to this admin app at `/login?returnUrl=<gateway
 * /authorize url>`. After the user authenticates here (email/password or Google), we finish
 * the OAuth flow by POSTing the freshly-issued MCG access token to the gateway's
 * `/authorize/continue` endpoint. That endpoint validates the token, sets the short-lived
 * authorization-server session cookie, and resumes the `/authorize` request so Claude/ChatGPT
 * receive their authorization code.
 *
 * The handoff is a top-level form POST (not XHR) so the gateway can set its session cookie in
 * a first-party context.
 *
 * The token's shop is the workspace the MCP connection is bound to. Login hands out the user's
 * last-used / first workspace, so every entry point goes through the `/mcp-connect` page first
 * (startMcpConnect), which lets a user who belongs to several workspaces — e.g. a team member of
 * someone else's shop — choose the one to connect.
 *
 * `/mcp-connect` is also the consent screen: it shows which app is asking (its self-declared name),
 * where the access goes (the redirect host) and what access it asks for, and nothing is handed to
 * the gateway until the user clicks Allow (or Deny). There is no automatic hand-off: any site can
 * register an OAuth client and send a user an authorize link, so being signed in must never be
 * enough to connect an app. The gateway enforces this as well (a single-use consent token from
 * `/authorize/consent` must accompany the hand-off).
 */

import { get as getCookie } from 'es-cookie'

// Origin of the MCG gateway / OAuth authorization server, derived from the API base URL.
// The access token must ONLY ever be POSTed back to this exact origin — never an arbitrary
// host — otherwise a crafted `returnUrl` could exfiltrate the token.
const gatewayOrigin = () => {
  try {
    return new URL(process.env.REACT_APP_URL).origin
  } catch {
    return null
  }
}

/**
 * Returns the stored MCG access token only if it is present and not expired (small skew),
 * so callers can decide whether the user is already signed in. Returns null otherwise.
 */
export const getValidStoredAccessToken = () => {
  const token = getCookie('token')
  if (!token) return null
  try {
    const part = token.split('.')[1]
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    const payload = JSON.parse(json)
    if (typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now() + 5000) {
      return null // expired (or within 5s of expiry)
    }
  } catch {
    return null // unparseable → treat as not usable
  }
  return token
}

const isValidAuthorizeReturnUrl = url => {
  if (!url) return false
  try {
    const parsed = new URL(url)
    const expected = gatewayOrigin()
    // Must target the gateway's own /authorize endpoint and no other origin.
    return (
      expected !== null &&
      parsed.origin === expected &&
      parsed.pathname.replace(/\/$/, '') === '/authorize'
    )
  } catch {
    return false
  }
}

/**
 * Returns the validated MCP `returnUrl` from the given (or current) location search, or null.
 * @param {string} [search] location.search; defaults to the current URL.
 * @returns {string|null}
 */
export const getMcpAuthorizeReturnUrl = (search = window.location.search) => {
  const returnUrl = new URLSearchParams(search).get('returnUrl')
  return isValidAuthorizeReturnUrl(returnUrl) ? returnUrl : null
}

/**
 * The parts of the pending `/authorize` request the consent screen shows when the gateway cannot
 * describe it (an older gateway without `/authorize/consent`). Read straight from the URL, so the
 * values are only what the requesting app put there.
 * @param {string} returnUrl validated gateway `/authorize` url.
 * @returns {{ clientId: string, redirectUri: string, redirectHost: string, redirectIsLoopback: boolean, scopes: string[] }|null}
 */
export const parseMcpAuthorizeRequest = returnUrl => {
  if (!isValidAuthorizeReturnUrl(returnUrl)) return null
  const params = new URL(returnUrl).searchParams
  const redirectUri = params.get('redirect_uri') || ''
  let redirectHost = ''
  let redirectIsLoopback = false
  try {
    const redirect = new URL(redirectUri)
    redirectHost = redirect.host
    redirectIsLoopback = ['127.0.0.1', '[::1]', 'localhost'].includes(redirect.hostname)
  } catch {
    redirectHost = redirectUri
  }
  const scopes = (params.get('scope') || '').split(' ').filter(Boolean)
  return {
    clientId: params.get('client_id') || '',
    redirectUri,
    redirectHost,
    redirectIsLoopback,
    scopes: [...new Set(scopes)],
  }
}

/**
 * URL of the gateway endpoint that describes a pending `/authorize` request for the consent screen
 * and issues the single-use consent token.
 * @param {string} returnUrl validated gateway `/authorize` url.
 */
export const mcpConsentDetailsUrl = returnUrl =>
  new URL('/authorize/consent', returnUrl).href

/** Route of the "choose a workspace to connect" step (src/pages/mcp-connect). */
export const MCP_CONNECT_PATH = '/mcp-connect'

/**
 * Continues the MCP connector OAuth flow after sign-in: navigates to the workspace picker, which
 * hands the (re-scoped) token to the gateway. The access token must already be stored in the
 * `token` cookie. Returns true if the navigation started (the caller must then NOT perform its
 * own redirect).
 * @param {string} returnUrl validated gateway `/authorize` url.
 * @returns {boolean}
 */
export const startMcpConnect = returnUrl => {
  if (!isValidAuthorizeReturnUrl(returnUrl)) return false
  window.location.href = `${MCP_CONNECT_PATH}?returnUrl=${encodeURIComponent(returnUrl)}`
  return true
}

/**
 * Submits a top-level POST to the gateway `/authorize/continue` with the MCG access token and the
 * user's decision on the consent screen, resuming the OAuth flow. Only ever call this from an
 * explicit Allow / Deny click. Returns true if the handoff was submitted (the caller must then NOT
 * perform its own redirect).
 * @param {string} accessToken MCG access token for the chosen workspace.
 * @param {string} returnUrl validated gateway `/authorize` url.
 * @param {{ consentToken?: string, decision: 'allow'|'deny' }} consent consentToken comes from the
 * gateway's `/authorize/consent`; an older gateway without it ignores both fields.
 * @returns {boolean}
 */
export const submitMcpAuthorizeContinue = (accessToken, returnUrl, consent) => {
  if (!accessToken || !isValidAuthorizeReturnUrl(returnUrl)) return false
  if (!consent || (consent.decision !== 'allow' && consent.decision !== 'deny')) return false

  const continueUrl = new URL('/authorize/continue', returnUrl).href

  const form = document.createElement('form')
  form.method = 'POST'
  form.action = continueUrl
  form.style.display = 'none'

  const addField = (name, value) => {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  }

  addField('accessToken', accessToken)
  addField('returnUrl', returnUrl)
  addField('decision', consent.decision)
  if (consent.consentToken) addField('consentToken', consent.consentToken)

  document.body.appendChild(form)
  form.submit()
  return true
}
